import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import FAQAccordion from './FAQAccordion';
import Seo from '../Seo';

const heroImage = '/award-logos/SAP1.png';
const secondaryImage = '/award-logos/SAP2.png';
const fallbackImage = '/services_detail_tech_1773741342619.png';

const capabilities = [
  {
    title: 'S/4HANA Digital Core Strategy',
    desc: 'We define the roadmap for your transition to the intelligent enterprise, evaluating Brownfield (conversion) vs. Greenfield (new build) paths to maximize long-term ROI and minimize operational disruption.'
  },
  {
    title: 'Precision Process Engineering',
    desc: 'Unify disparate business functions — from finance and procurement to manufacturing and sales — into a single, high-fidelity SAP ecosystem governed by global best practices.'
  },
  {
    title: 'Advanced Governance & Risk (GRC)',
    desc: 'Embed automated auditability, robust role-based access control (RBAC), and sovereign data standards directly into your SAP landscape to ensure continuous compliance and security.'
  },
  {
    title: 'Intelligent Supply Chain (SCM)',
    desc: 'Leverage SAP IBP and MM modules to synchronize supply and demand, reducing inventory carrying costs while improving fulfillment velocity through real-time visibility.'
  },
  {
    title: 'Human Capital Transformation',
    desc: 'Modernize the workforce experience using SAP SuccessFactors, integrating talent management, payroll, and core HR with your enterprise ERP for data-driven people decisions.'
  },
  {
    title: 'Real-Time Financial Analytics',
    desc: 'Deploy SAP Central Finance and FI/CO optimizations to achieve a "continuous close," reducing manual reconciliation effort and providing leadership with instant margin visibility.'
  }
];

const modules = [
  { 
    title: 'SAP S/4HANA Transformation', 
    points: [
      'Greenfield, Brownfield, and Selective Data Transition',
      'Fiori UX implementation for enhanced productivity',
      'Embedded Analytics and Machine Learning integration'
    ] 
  },
  { 
    title: 'Line of Business (LoB) Optimization', 
    points: [
      'FI/CO (Finance), SD (Sales), and MM (Procurement)',
      'Ariba Network and SuccessFactors integration',
      'SAP IBP for advanced supply chain planning'
    ] 
  },
  { 
    title: 'Data Migration & Quality Assurance', 
    points: [
      'Legacy discovery and data cleansing strategy',
      'SAP Data Services (BODS) and Migration Cockpit (LTMC)',
      'Automated testing with SAP Solution Manager / Tricentis'
    ] 
  },
  { 
    title: 'Managed Services & SAP Optimization', 
    points: [
      '24/7 Functional & Technical AMS support',
      'Performance tuning and custom code optimization',
      'Quarterly release management and patch governance'
    ] 
  }
];

const executionSteps = [
  { phase: 'Discovery & Prep', desc: 'Baseline assessment of technical debt, business process maturity, and S/4HANA readiness using SAP Transformation Navigator.' },
  { phase: 'Design & Exploration', desc: 'Fit-to-standard analysis and blueprinting sessions to align business requirements with SAP best practices.' },
  { phase: 'Realization & Build', desc: 'Agile configuration and custom development (ABAP on HANA) using SAP Activate and SolMan governance.' },
  { phase: 'Final Validation', desc: 'End-to-end integration testing (SIT), volume testing, and rigorous UAT with specific business scenarios.' },
  { phase: 'Cutover & Deployment', desc: 'Meticulously planned hyper-care period and risk-managed transition to production with zero-loss data migration.' },
  { phase: 'Run & Continuous Value', desc: 'Long-term AMS support focused on functional enhancements, technical health, and maximizing SAP license value.' }
];



const industries = ['Manufacturing', 'Retail', 'Healthcare', 'Insurance', 'Utilities', 'Automotive', 'Professional Services'];

const faqs = [
  {
    q: 'How does Libsys help us choose between Brownfield and Greenfield approaches?',
    a: 'We conduct a "Technical Readiness Assessment" that looks at your current customization depth, technical debt, and business process alignment with S/4HANA standards. If your current ECC system is heavily customized or messy, a Greenfield (new build) approach often yields better ROI by resetting to best practices. If your data is clean and processes are sound, a Brownfield (conversion) may be the faster, more cost-effective path. We provide a weighted recommendation based on your specific risk profile and growth goals.'
  },
  {
    q: 'How do you ensure data integrity during a legacy-to-SAP migration?',
    a: 'Data integrity is managed through our proprietary "Clean-Data Framework." We start with automated data profiling to identify duplicates and inconsistencies in your legacy system. Using SAP Data Services and Migration Cockpit, we perform iterative mock migrations (Sprints) into sandbox environments. Each cycle involves automated reconciliation and sign-off from business owners, ensuring that by the time we reach production cutover, the data is pre-validated and audit-ready.'
  },
  {
    q: 'Can you support niche SAP modules like SuccessFactors and Ariba?',
    a: 'Yes. Our SAP practice extends beyond the core ERP into the broader ecosystem. We specialize in "Connected Enterprise" scenarios where S/4HANA acts as the digital core, integrated seamlessly with SuccessFactors for HXM, Ariba for Spend Management, and IBP for Integrated Business Planning. We manage the API-led integrations and cross-module process flows to ensure data consistency across your entire landscape.'
  },
  {
    q: 'What is your post-go-live support model (AMS)?',
    a: 'Our Application Management Services (AMS) are designed to provide long-term stability and value. We offer a tiered support model (L1/L2/L3) covering functional optimization, ABAP development, and BASIS technical support. Unlike generic support, our AMS focuses on continuous improvement — identifying recurring issues and proposing process optimizations or technical patches to reduce ticket volume and improve system performance over time.'
  },
  {
    q: 'How do you manage Change Management during a major SAP rollout?',
    a: 'Technology is only 40% of the battle; the rest is people. We embed "Adoption Champions" into each business unit to drive user engagement. Our delivery includes role-based training modules, Fiori UX optimization to simplify interfaces, and structured communication plans. We measure adoption through system usage analytics and feedback loops, ensuring that the technology actually gets used to its full potential by your workforce.'
  },
  {
    q: 'Does Libsys support SAP Cloud and Hybrid deployments?',
    a: 'Absolutely. Whether you are looking at RISE with SAP (Public or Private Cloud) or a custom hybrid landscape, we manage the infrastructure architecture, security configuration, and connectivity. We help bridge the gap between on-premise components and cloud-native services, ensuring a secure, high-performance environment that scales with your business.'
  }
];

const ServiceSAP = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white min-h-screen pt-8 [&_.text-h1]:text-h2 [&_.text-h2]:text-h3 [&_.text-body]:text-small [&_.text-small]:text-subtext">
      <Seo
        title="SAP Services"
        description="Libsys SAP consulting — S/4HANA migration, finance and logistics integration, application management, and SAP transformation programs for scalable enterprise operations."
        path="/services/sap"
      />
      {/* ══ HERO SECTION ══════════════════════════════════════════════════════ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-12">
        <div className="rounded-[2.5rem] border border-slate-200 bg-slate-50/70 p-6 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="lg:col-span-5">
              <span className="inline-flex items-center gap-2 text-strategic-tagline mb-5">
                Enterprise SAP Solutions
              </span>
              <h1 className="text-h1 text-primary mb-5">
                SAP Transformation: Building the Intelligent Digital Core
              </h1>
              <p className="text-slate-600 text-body leading-[1.7] font-medium mb-6 max-w-2xl">
                Libsys delivers enterprise-grade SAP programs designed for scale, resilience, and operational clarity. We specialize in guiding organizations through complex S/4HANA transitions, ensuring that your ERP becomes a strategic asset rather than a technical burden. Our delivery combines deep functional domain expertise with rigorous technical execution to unify finance, supply chain, and HXM under a single governed ecosystem. By focusing on process-first technical execution and comprehensive data cleansing, we ensure a zero-disruption transition to the intelligent digital core.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 mb-8">
                {[
                  'S/4HANA Strategy & Roadmap',
                  'Process-first Technical Execution',
                  'End-to-End Governance & GRC',
                  'Automated Migration Workflows',
                  'Fiori UX & Productivity Design',
                  '24/7 Managed Services (AMS)'
                ].map((item) => (
                  <li key={item} className="text-slate-700 text-subtext font-bold leading-relaxed flex items-start gap-2">
                    <span className="w-2 h-2 rounded-full bg-secondary mt-[7px] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-4">
                <a
                  href="tel:+16307991556"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-primary hover:bg-[#254b85] text-white font-black text-[11px] uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-blue-900/10 active:scale-95 group"
                >
                  Schedule SAP Consultation
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="lg:col-span-7 lg:-mt-12">
              <div className="rounded-[2.5rem] overflow-hidden border border-slate-200 shadow-[0_30px_70px_-15px_rgba(35,96,134,0.2)] bg-slate-100 relative group">
                <div className="absolute inset-0 bg-primary/5 group-hover:bg-transparent transition-colors z-10 pointer-events-none" />
                <img src={heroImage} alt="SAP Enterprise Environment" className="w-full h-[400px] lg:h-[600px] object-cover group-hover:scale-105 transition-transform duration-1000" onError={(e) => { e.currentTarget.src = fallbackImage; }} />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ CORE CAPABILITIES ════════════════════════════════════════════════ */}
      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-14 text-center">
            <span className="text-strategic-tagline block mb-2">Technical Capabilities</span>
            <h2 className="text-h2 text-primary">Strategic Depth Across the SAP Lifecycle</h2>
            <p className="text-slate-600 text-body max-w-3xl mx-auto mt-4 leading-relaxed">
              We move organizations from legacy ERP constraints to modern SAP flexibility by integrating high-precision technical execution with deep business process re-engineering.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((item) => (
              <div key={item.title} className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <h3 className="text-[22px] font-black text-primary mb-4 leading-tight">{item.title}</h3>
                <p className="text-slate-600 text-subtext leading-relaxed font-medium">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ SERVICE PORTFOLIO ════════════════════════════════════════════════ */}
      <section className="py-16 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="rounded-[2.5rem] border border-slate-200 bg-slate-50/70 overflow-hidden shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-stretch">
            <div className="lg:col-span-8 p-8 lg:p-12">
              <span className="text-strategic-tagline block mb-3">Service Modules</span>
              <h2 className="text-h2 text-primary mb-6">Connected Enterprise Enablement</h2>
              <p className="text-slate-600 text-body mb-8 max-w-3xl">
                Our modular service approach allows for targeted improvements in specific business functions while ensuring full alignment with your broader enterprise architecture.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {modules.map((module) => (
                  <div key={module.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h3 className="text-body font-black text-primary mb-4 flex items-center gap-2">
                       {module.title}
                    </h3>
                    <ul className="space-y-3">
                      {module.points.map((point) => (
                        <li key={point} className="text-slate-600 text-subtext font-semibold leading-relaxed flex items-start gap-2.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-2 flex-shrink-0" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-4 border-t lg:border-t-0 lg:border-l border-slate-200 bg-slate-100 relative overflow-hidden">
               <img src={secondaryImage} alt="Strategic implementation" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.src = fallbackImage; }} />
               <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent flex flex-col justify-end p-8">
                  <p className="text-white text-lg font-black leading-tight italic">"Delivering zero-disruption SAP transformation for global manufacturing and retail players."</p>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ DELIVERY PHASES ══════════════════════════════════════════════════ */}
      <section className="py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-12 mb-10">
              <span className="text-strategic-tagline block mb-3">The Libsys Framework</span>
              <h2 className="text-h2 text-primary mb-6">A Governed Path to SAP Maturity</h2>
              <p className="text-slate-600 text-body max-w-4xl">
                 We follow a refined adaptation of the SAP Activate methodology, emphasizing early value discovery and strict governance at every architectural checkpoint.
              </p>
            </div>
            <div className="lg:col-span-12">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-10">
                {executionSteps.map((step, idx) => (
                  <div key={step.phase} className="relative pl-12">
                    <div className="absolute left-0 top-0 text-5xl font-black text-slate-200 leading-none">
                      {(idx + 1).toString().padStart(2, '0')}
                    </div>
                    <div className="relative">
                      <h4 className="text-body font-black text-primary mb-2 uppercase tracking-wide">{step.phase}</h4>
                      <p className="text-slate-600 text-subtext leading-relaxed font-medium">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ ECOSYSTEM SECTION ════════════════════════════════════════════════ */}
      <section className="py-16 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-12">
             <span className="text-strategic-tagline block mb-3 text-center lg:text-left">Technology Ecosystem</span>
             <h2 className="text-h2 text-primary mb-6 text-center lg:text-left">Integrating Every Dimension of the SAP Landscape</h2>
             <p className="text-slate-600 text-body mb-10 max-w-4xl text-center lg:text-left mx-auto lg:mx-0">
                A modern ERP does not exist in isolation. Libsys specializes in building "Connected Enterprise" scenarios where your core SAP system integrates seamlessly with specialized line-of-business cloud services, ensuring data continuity and executive visibility across the entire value chain.
             </p>
             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { title: 'Digital Core', items: ['S/4HANA Cloud', 'S/4HANA On-Premise', 'SAP ECC 6.0'] },
                  { title: 'People & Spend', items: ['SuccessFactors', 'Ariba Network', 'SAP Fieldglass'] },
                  { title: 'Insight & Planning', items: ['SAP IBP', 'SAP Analytics Cloud (SAC)', 'BW/4HANA'] },
                  { title: 'UX & Extensions', items: ['SAP Fiori', 'BTP (Business Tech Platform)', 'AppGyver'] }
                ].map(cat => (
                  <div key={cat.title} className="p-5 rounded-2xl border border-slate-200 bg-slate-50/50 hover:bg-white hover:shadow-lg transition-all">
                     <h5 className="font-black text-primary text-tiny uppercase tracking-widest mb-3">{cat.title}</h5>
                     <div className="flex flex-wrap gap-2">
                        {cat.items.map(i => (
                          <span key={i} className="px-2.5 py-1 bg-white border border-slate-200 rounded-md text-tiny font-bold text-slate-600">{i}</span>
                        ))}
                     </div>
                  </div>
                ))}
             </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch pt-12 border-t border-slate-100">
             <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm h-full">
                <h3 className="text-xl font-black text-primary mb-6">Industries We Modernize</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                   {industries.map(ind => (
                     <div key={ind} className="flex items-center justify-between border-b border-slate-100 pb-3 last:border-b-0 last:pb-0">
                        <span className="text-slate-700 font-bold text-subtext">{ind}</span>
                        <span className="w-1.5 h-1.5 rounded-full bg-secondary" />
                     </div>
                   ))}
                </div>
             </div>
             <div className="flex flex-col gap-6">
                <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm flex-1 flex flex-col justify-center">
                   <p className="text-primary text-xl font-black leading-tight italic">
                      "We help global organizations reduce TCO by up to 30% through disciplined landscape consolidation and S/4HANA modernization."
                   </p>
                   <p className="text-slate-500 text-sm font-bold mt-4 uppercase tracking-[0.2em]">— Enterprise ROI Performance</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-3xl p-6 text-center">
                   <p className="text-slate-600 text-subtext font-medium">Focused on delivery excellence and zero-loss data transition since 2002.</p>
                </div>
             </div>
          </div>
      </section>

      <FAQAccordion faqs={faqs} />
    </div>
  );
};

export default ServiceSAP;
